import { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import './RateLimitCountdown.css';

const MAX_RETRIES = 3;
const DEFAULT_WAIT = 30;
const MAX_WAIT = 300;

interface RateLimitCountdownProps {
  onRetry: () => Promise<void>;
  onSettings?: () => void;
}

export interface RateLimitCountdownHandle {
  start: (retryAfterSec: number) => void;
  isActive: () => boolean;
}

export default forwardRef<RateLimitCountdownHandle, RateLimitCountdownProps>(function RateLimitCountdown({ onRetry, onSettings }, ref) {
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [attempt, setAttempt] = useState<number>(0);
  const [gaveUp, setGaveUp] = useState<boolean>(false);
  const [retrying, setRetrying] = useState<boolean>(false);
  const waitRef = useRef<number>(DEFAULT_WAIT);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCountdown = useCallback((retryAfterSec: number) => {
    const wait = retryAfterSec || waitRef.current;
    waitRef.current = Math.min(wait * 2, MAX_WAIT);
    setSecondsLeft(wait);
    setGaveUp(false);
    setRetrying(false);

    if (timerRef.current !== null) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (timerRef.current !== null) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => { if (timerRef.current !== null) clearInterval(timerRef.current); };
  }, []);

  // When countdown reaches 0 and we haven't given up, auto-retry
  useEffect(() => {
    if (secondsLeft === 0 && attempt > 0 && !gaveUp && !retrying) {
      if (attempt >= MAX_RETRIES) {
        setGaveUp(true);
        return;
      }
      setRetrying(true);
      const nextAttempt = attempt + 1;
      setAttempt(nextAttempt);
      onRetry().then(() => {
        // Success — reset
        setAttempt(0);
        setGaveUp(false);
        setRetrying(false);
      }).catch((err: { status?: number; retryAfter?: number }) => {
        setRetrying(false);
        if (err?.status === 429) {
          startCountdown(err.retryAfter as number);
        } else {
          // Non-429 error — stop retrying, let normal error handling take over
          setAttempt(0);
          setGaveUp(false);
        }
      });
    }
  }, [secondsLeft, attempt, gaveUp, retrying, onRetry, startCountdown]);

  useImperativeHandle(ref, () => ({
    start: (retryAfterSec: number) => {
      setAttempt(1);
      waitRef.current = DEFAULT_WAIT;
      startCountdown(retryAfterSec);
    },
    isActive: () => attempt > 0,
  }), [startCountdown]);

  const handleManualRetry = useCallback(() => {
    setAttempt(0);
    setGaveUp(false);
    setRetrying(false);
    waitRef.current = DEFAULT_WAIT;
    onRetry().catch(() => {});
  }, [onRetry]);

  if (attempt === 0 && !gaveUp) return null;

  if (gaveUp) {
    return (
      <div className="rate-limit-area">
        <div className="rate-limit-card">
          <p className="rate-limit-msg">Still rate limited after {MAX_RETRIES} attempts. Try again later or switch to a different model in Settings.</p>
          <div className="rate-limit-actions">
            <button className="rate-limit-retry-btn" onClick={handleManualRetry}>Retry</button>
            {onSettings && <button className="rate-limit-settings-btn" onClick={onSettings}>Settings</button>}
          </div>
        </div>
      </div>
    );
  }

  if (secondsLeft > 0) {
    return (
      <div className="rate-limit-area">
        <div className="rate-limit-card">
          <p className="rate-limit-msg">API rate limit hit. Retrying automatically in {secondsLeft}s...</p>
          <div className="rate-limit-progress">
            <div className="rate-limit-bar" />
          </div>
        </div>
      </div>
    );
  }

  if (retrying) {
    return (
      <div className="rate-limit-area">
        <div className="rate-limit-card">
          <p className="rate-limit-msg">Retrying... (attempt {attempt} of {MAX_RETRIES})</p>
        </div>
      </div>
    );
  }

  return null;
});
